import { AUTH_API, fetchWithAuth } from './api';

/**
 * Extract subdomain from current URL
 * @returns {string|null} - The subdomain or null if none
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Skip localhost and IP addresses
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return null;
  }
  
  // Split hostname and check for subdomain
  const parts = hostname.split('.');
  
  // If we have more than 2 parts (subdomain.domain.com), return the first part
  if (parts.length > 2) {
    const subdomain = parts[0];
    
    // Exclude common subdomains that aren't conventions
    const excludedSubdomains = ['www', 'api', 'admin', 'app', 'staging', 'dev'];
    if (!excludedSubdomains.includes(subdomain)) {
      return subdomain;
    }
  }
  
  return null;
};

/**
 * Check if current domain is a convention subdomain
 * @returns {boolean}
 */
export const isConventionSubdomain = () => {
  return getSubdomain() !== null;
};

/**
 * Add convention access for the current user
 * @param {string} conventionSubdomain - The convention subdomain to add
 * @returns {Promise<Object>} - Response with updated conventions
 */
export const addConventionAccess = async (conventionSubdomain) => {
  try {
    const response = await fetchWithAuth(`${AUTH_API}/add-convention`, {
      method: 'POST',
      body: { convention: conventionSubdomain }
    });
    
    return response;
  } catch (error) {
    console.error('Error adding convention access:', error);
    throw error;
  }
};

/**
 * Add convention access for a specific convention
 * @param {string} conventionName - The convention name to add
 * @param {Array} userConventions - Optional array of user's current conventions to avoid duplicate calls
 * @returns {Promise<Object|null>} - Convention access result
 */
export const handleSpecificConventionAccess = async (conventionName, userConventions = null) => {
  if (!conventionName) {
    console.log('No convention name provided');
    return null;
  }
  
  console.log(`Processing convention access for: ${conventionName}`);
  
  // If user conventions are provided, check if they already have access
  if (userConventions && Array.isArray(userConventions) && userConventions.includes(conventionName)) {
    console.log(`User already has access to convention: ${conventionName}`);
    return { message: 'Convention already accessible', convention: conventionName };
  }
  
  try {
    const result = await addConventionAccess(conventionName);
    console.log('Convention access result:', result);
    return result;
  } catch (error) {
    console.error('Failed to add convention access:', error);
    throw error;
  }
};

/**
 * Handle convention access when user visits a subdomain
 * This should be called when a user accesses a convention subdomain
 * @param {Array} userConventions - Optional array of user's current conventions to avoid duplicate calls
 * @returns {Promise<Object|null>} - Convention access result or null if no subdomain
 */
export const handleConventionAccess = async (userConventions = null) => {
  const subdomain = getSubdomain();
  
  if (!subdomain) {
    console.log('No convention subdomain detected');
    return null;
  }
  
  return await handleSpecificConventionAccess(subdomain, userConventions);
};

/**
 * Get the main domain URL (without subdomain)
 * @returns {string} - The main domain URL
 */
export const getMainDomainUrl = () => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // If localhost, return as-is
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  
  // For dance.digital domains, always redirect to www.dance.digital
  if (hostname.includes('dance.digital')) {
    return `${protocol}//www.dance.digital`;
  }
  
  // Remove subdomain for other production domains
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const mainDomain = parts.slice(1).join('.');
    return `${protocol}//${mainDomain}${port ? `:${port}` : ''}`;
  }
  
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

/**
 * Redirect to main domain with optional path
 * @param {string} path - Optional path to redirect to
 */
export const redirectToMainDomain = (path = '') => {
  const mainDomainUrl = getMainDomainUrl();
  window.location.href = `${mainDomainUrl}${path}`;
};

/**
 * Check if user has access to a specific convention
 * @param {Array} userConventions - Array of user's accessible conventions
 * @param {string} conventionSubdomain - Convention to check access for
 * @returns {boolean}
 */
export const hasConventionAccess = (userConventions, conventionSubdomain) => {
  if (!userConventions || !Array.isArray(userConventions)) {
    return false;
  }
  
  return userConventions.includes(conventionSubdomain);
};

/**
 * Get convention-specific landing page URL
 * @param {string} conventionSubdomain - The convention subdomain
 * @returns {string} - The convention landing page URL
 */
export const getConventionLandingUrl = (conventionSubdomain) => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // For localhost, just return the main domain
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  
  // For production, create subdomain URL
  const parts = hostname.split('.');
  const mainDomain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
  
  return `${protocol}//${conventionSubdomain}.${mainDomain}${port ? `:${port}` : ''}`;
}; 